<template>
  <div
    ref="menuWrapper"
    class="sam-menu"
    :class="{ 'is-open': isMenuOpen }"
    data-samora
  >
    <a
      ref="menuActivator"
      class="sam-menu__activator"
    >
      <slot
        name="activator"
        v-bind="slotProps"
      />
    </a>

    <div
      ref="menuContent"
      v-bind="$attrs"
      class="menu__content"
    >
      <slot v-bind="slotProps" />
    </div>
  </div>
</template>

<script>
import initialize from '../../../plugins/menu';

const EMIT = {
  OPEN: 'open',
  CLOSE: 'close',
};

export default {
  name: 'SamMenu',
  inheritAttrs: false,
  props: {
    activateOnHover: {
      type: Boolean,
      required: false,
      default: false,
    },
    closeOnContentClick: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Propagate click event from activator to menu content. Use it if you have
     * a select aside with other selects and the select content overlaps eachother when
     * opened.
     */
    propagateClick: {
      type: Boolean,
      required: false,
      default: false,
    },
    options: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  data: () => ({
    menu: null,
  }),
  computed: {
    // to pass down to slot elements
    slotProps() {
      return {
        // data
        isMenuOpen: this.isMenuOpen,

        // actions
        toggle: this.toggleMenu,
        close: this.close,
        open: this.open,

        // event binding
        on: {
          click: !this.activateOnHover && this.toggleMenu,
        },
      };
    },
    isMenuOpen() {
      return this.menu?.isOpen ?? false;
    },
  },
  mounted() {
    this.menu = initialize(
      this.$refs.menuActivator.firstChild,
      this.$refs.menuContent,
      this.options
    );

    // add hover events if menu activates on hover
    if (this.activateOnHover) {
      this.$refs.menuWrapper.addEventListener('mouseenter', this.open);
      this.$refs.menuWrapper.addEventListener('mouseleave', this.close);
    }

    document.addEventListener('click', this.handleOutsideClick, { capture: true });

    if (this.closeOnContentClick) {
      this.$refs.menuContent.addEventListener('click', this.handleCloseOnClick);
    }
  },
  beforeDestroy() {
    // eslint-disable-next-line no-unused-expressions
    this.menu?.cleanup?.();
    document.removeEventListener('click', this.handleOutsideClick);
    this.$refs.menuContent.removeEventListener('click', this.handleCloseOnClick);
    this.$refs.menuWrapper.removeEventListener('mouseenter', this.open);
    this.$refs.menuWrapper.removeEventListener('mouseleave', this.close);
  },
  methods: {
    /**
     * Toggle menu opened state
     * @param {Event} $event
     */
    toggleMenu($event) {
      if (!this.propagateClick) {
        $event.stopPropagation();
      }

      // eslint-disable-next-line no-unused-expressions
      this.menu && this.isMenuOpen ? this.close() : this.open();
    },
    /**
     * Force menu open
     */
    open() {
      // eslint-disable-next-line no-unused-expressions
      this.menu?.show?.();
      this.$emit(EMIT.OPEN, {
        __menuContent: this.$refs.menuContent,
        __slotProps: this.slotProps,
        __menu: this.menu,
      });
    },
    /**
     * Force menu close
     */
    close() {
      // eslint-disable-next-line no-unused-expressions
      this.menu?.hide?.();
      this.$emit(EMIT.CLOSE, {
        __menuContent: this.$refs.menuContent,
        __slotProps: this.slotProps,
        __menu: this.menu,
      });
    },
    /**
     * Checks for outside click from menu
     * @param $event native click event
     */
    handleOutsideClick($event) {
      if (!this.$refs.menuWrapper?.contains($event.target)) this.close();
    },
    /**
     * Checks for menu content click
     * @param $event native click event
     */
    handleCloseOnClick($event) {
      if (this.$refs.menuContent?.contains($event.target)) this.close();
    },
  },
};
</script>

<style scoped lang="scss">
.sam-menu {
  position: relative;
  display: inline-block;

  .menu__content {
    @extend .elevation--3, .shadow--dropdown;
    position: fixed;
    display: none;
    background-color: $pure--white;
    border-radius: $size--1;
    border: $size--0--fourth solid $neutral--30;
    box-shadow: 0 $size--0--fourth $size--1 $size--0--fourth rgba(105, 108, 114, 0.22);
    min-width: 248px;
  }
}
</style>
